/** Labels analytics days per league and combined, with a comparison line against earlier days. */

import { groupBy } from './groupBy';
import { getLeagueLabel, getParentLeague, type LeagueKey } from './league';
import {
  callDirection,
  classifyDay,
  type DirectionPick,
  type DirectionResult,
  type MarketCondition,
  PICK_DIRECTIONS,
  type PickDirection,
} from './marketCondition';
import { addDays, type CalendarDay } from './time';

/** Hit rate minus break-even, the measure a day is compared on; null with no picks. */
function directionMargin(result: DirectionResult): number | null {
  if (result.picks === 0 || result.breakeven === null) return null;
  return result.hits / result.picks - result.breakeven;
}

type HistoryComparisonKind = 'best' | 'worst' | 'better' | 'worse' | 'typical' | 'not_enough';

export interface HistoryComparison {
  kind: HistoryComparisonKind;
  /** Earlier days with picks it was compared against */
  pastDays: number;
}

/** Best or worst at the extreme, better or worse beyond one standard deviation, else typical. */
export function compareToHistory(today: number, pastMargins: number[]): HistoryComparison {
  const pastDays = pastMargins.length;
  if (pastDays < 2) return { kind: 'not_enough', pastDays };
  if (today > Math.max(...pastMargins)) return { kind: 'best', pastDays };
  if (today < Math.min(...pastMargins)) return { kind: 'worst', pastDays };
  const mean = pastMargins.reduce((a, b) => a + b, 0) / pastDays;
  const spread = Math.sqrt(pastMargins.reduce((a, m) => a + (m - mean) ** 2, 0) / (pastDays - 1));
  if (today > mean + spread) return { kind: 'better', pastDays };
  if (today < mean - spread) return { kind: 'worse', pastDays };
  return { kind: 'typical', pastDays };
}

export function describeComparison(
  comparison: HistoryComparison,
  direction: PickDirection,
  historyLeagueLabel: string,
): string {
  const side = `${direction}s`;
  switch (comparison.kind) {
    case 'best':
      return `Best ${side} day for the ${historyLeagueLabel} in its last ${comparison.pastDays}`;
    case 'worst':
      return `Worst ${side} day for the ${historyLeagueLabel} in its last ${comparison.pastDays}`;
    case 'better':
      return `${side} better than usual for the ${historyLeagueLabel}`;
    case 'worse':
      return `${side} worse than usual for the ${historyLeagueLabel}`;
    case 'typical':
      return `${side}: a typical ${historyLeagueLabel} day`;
    case 'not_enough':
      return `Not enough ${historyLeagueLabel} days yet to compare`;
  }
}

// =============================================================================
// Labelling days from graded picks
// =============================================================================

/** How far back a league's earlier days are looked at for the comparison line. */
export const HISTORY_WINDOW_DAYS = 60;

export interface LabelPick {
  day: CalendarDay;
  leagueKey: LeagueKey;
  direction: PickDirection;
  hit: boolean;
  breakeven: number;
}

export interface LeagueMarketCondition {
  leagueKey: LeagueKey;
  condition: MarketCondition;
  over: DirectionResult;
  under: DirectionResult;
  overComparison: string | null;
  underComparison: string | null;
}

export interface DayMarketCondition {
  condition: MarketCondition;
  over: DirectionResult;
  under: DirectionResult;
  /** Every graded Over and Under pick together, for the day's overall hit rate */
  overall: DirectionResult;
  leagueConditions: LeagueMarketCondition[];
}

function directionPicks(picks: LabelPick[], direction: PickDirection): DirectionPick[] {
  return picks
    .filter((p) => p.direction === direction)
    .map(({ hit, breakeven }) => ({ hit, breakeven }));
}

/** Preseason and summer league are compared against their parent league's days. */
function historyLeague(leagueKey: LeagueKey): LeagueKey {
  return getParentLeague(leagueKey);
}

/** `picks` should reach HISTORY_WINDOW_DAYS before the first day, for the comparison lines. */
export function labelDays(
  days: CalendarDay[],
  picks: LabelPick[],
): Map<CalendarDay, DayMarketCondition> {
  const picksByDay = groupBy(picks, (p) => p.day);
  // Margins per history league, direction and day, for the comparison lines
  const marginsByHistory = new Map<string, Map<CalendarDay, number>>();
  for (const [historyKey, historyPicks] of groupBy(picks, (p) => historyLeague(p.leagueKey))) {
    for (const direction of Object.values(PICK_DIRECTIONS)) {
      const byDay = new Map<CalendarDay, number>();
      for (const [day, dayPicks] of groupBy(historyPicks, (p) => p.day)) {
        const margin = directionMargin(callDirection(directionPicks(dayPicks, direction)));
        if (margin !== null) byDay.set(day, margin);
      }
      marginsByHistory.set(`${historyKey}|${direction}`, byDay);
    }
  }

  const comparisonFor = (
    leagueKey: LeagueKey,
    direction: PickDirection,
    day: CalendarDay,
    result: DirectionResult,
  ): string | null => {
    const today = directionMargin(result);
    if (today === null) return null;
    const historyKey = historyLeague(leagueKey);
    const windowStart = addDays(day, -HISTORY_WINDOW_DAYS);
    const past = [...(marginsByHistory.get(`${historyKey}|${direction}`) ?? new Map())]
      .filter(([pastDay]) => pastDay >= windowStart && pastDay < day)
      .map(([, margin]) => margin);
    return describeComparison(compareToHistory(today, past), direction, getLeagueLabel(historyKey));
  };

  const labelled = new Map<CalendarDay, DayMarketCondition>();
  for (const day of days) {
    const dayPicks = picksByDay.get(day) ?? [];
    const over = callDirection(directionPicks(dayPicks, 'Over'));
    const under = callDirection(directionPicks(dayPicks, 'Under'));
    const leagueConditions = [...groupBy(dayPicks, (p) => p.leagueKey)]
      .map(([leagueKey, leaguePicks]): LeagueMarketCondition => {
        const leagueOver = callDirection(directionPicks(leaguePicks, 'Over'));
        const leagueUnder = callDirection(directionPicks(leaguePicks, 'Under'));
        return {
          leagueKey,
          condition: classifyDay(leagueOver.call, leagueUnder.call),
          over: leagueOver,
          under: leagueUnder,
          overComparison: comparisonFor(leagueKey, 'Over', day, leagueOver),
          underComparison: comparisonFor(leagueKey, 'Under', day, leagueUnder),
        };
      })
      .sort((a, b) => b.over.picks + b.under.picks - (a.over.picks + a.under.picks));
    labelled.set(day, {
      condition: classifyDay(over.call, under.call),
      over,
      under,
      overall: callDirection(dayPicks.map(({ hit, breakeven }) => ({ hit, breakeven }))),
      leagueConditions,
    });
  }
  return labelled;
}

/** A league's comparison lines, each once: both directions can say "Not enough WNBA days yet to compare". */
export function comparisonLines(league: LeagueMarketCondition): string[] {
  return [...new Set([league.overComparison, league.underComparison])].filter(
    (line): line is string => line !== null,
  );
}

/** One sentence for the day, shown under the label. */
export function describeDay(day: DayMarketCondition): string {
  const part = (direction: PickDirection, result: DirectionResult): string => {
    if (result.picks === 0 || result.breakeven === null) return `no ${direction.toLowerCase()}s`;
    const hitRate = Math.round((result.hits / result.picks) * 100);
    const breakeven = Math.round(result.breakeven * 100);
    return `${direction}s hit ${hitRate}% (break-even ${breakeven}%)`;
  };
  return `${part('Over', day.over)}, ${part('Under', day.under)}`;
}
